import type { RunScreenerQueryStateAtomValue } from "components/screener/v2/atoms";
import { runScreenerQueryStateAtom } from "components/screener/v2/atoms";
import classNames from "components/ui/classNames";
import { TextTooltip } from "components/ui/Tooltip";
import { secondsToHours } from "date-fns";
import { useAtomValue } from "jotai";
import { selectAtom } from "jotai/utils";
import { InfinityIcon } from "lucide-react";
import { useIntl } from "react-intl";

interface RateLimitProps {
  className?: string;
}
const getRateLimit = (state: RunScreenerQueryStateAtomValue) => state?.rateLimit;
export const RateLimit = ({ className }: RateLimitProps) => {
  const intl = useIntl();
  const rateLimit = useAtomValue(selectAtom(runScreenerQueryStateAtom, getRateLimit));
  if (!rateLimit) {
    return null;
  }
  const content = intl.formatMessage(
    {
      defaultMessage: "Limite de uso. O mesmo será liberado novamente {retryAfter}",
      id: "jSbTun",
    },
    {
      retryAfter: intl.formatRelativeTime(secondsToHours(rateLimit.retryAfter), "hours"),
    },
  );
  const percentConsumed = Math.min(
    1,
    Math.max(Math.max(rateLimit.limit - rateLimit.remaining, 0) / rateLimit.limit, 0),
  );
  return (
    <div
      className={classNames(
        "flex max-h-6 items-center gap-2 rounded-lg border px-2 text-xs dark:font-bold",
        {
          "dark:border-slate-900 dark:bg-slate-600": percentConsumed < 0.5,
          "dark:border-transparent dark:bg-yellow-400/20 dark:text-yellow-200":
            percentConsumed >= 0.5 && percentConsumed < 0.8,
          "dark:border-transparent dark:bg-rose-400/20 dark:text-rose-200": percentConsumed >= 0.8,
        },
        className,
      )}
    >
      <InfinityIcon className="size-5" absoluteStrokeWidth={true} />
      <TextTooltip
        asChild={true}
        side="top"
        align="center"
        delayDuration={1500}
        contentClassName="font-bold"
        content={content}
      >
        <span className={classNames("flex min-h-6 select-none items-center", "whitespace-nowrap")}>
          {Math.max(rateLimit.limit - rateLimit.remaining, 0)} / {rateLimit.limit}
        </span>
      </TextTooltip>
    </div>
  );
};
